package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.sdk.currentUser
import com.ilussobsa.sdk.currentUserSession
import com.ilussobsa.sdk.currentUserSessionNullable
import com.ilussobsa.sdk.masqueradeUserId
import com.ilussobsa.utils.priceField
import com.lightningkite.kiteui.ExternalServices
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogPageNavigator
import com.lightningkite.kiteui.navigation.mainPageNavigator
import com.lightningkite.kiteui.navigation.pageNavigator
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.kiteui.views.l2.lazyExpanding
import com.lightningkite.lightningdb.modification
import com.lightningkite.now
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import com.lightningkite.serialization.lensPath

enum class VerificationStep { StepZeroCreateAccount, StepOneHowWillYouPay, StepTwoCreditCardVerify }

fun stepForVerificationForm(user: User?, showStepIfComplete: Boolean): VerificationStep? = when {
    user == null -> VerificationStep.StepZeroCreateAccount
    user.role >= UserRole.Manager -> null
    user.financingSource == null -> VerificationStep.StepOneHowWillYouPay
    user.paymentSetup == null || showStepIfComplete -> VerificationStep.StepTwoCreditCardVerify
    else -> null
}

fun ViewWriter.verificationForm(
    showSuccessIfComplete: MutableReactive<Boolean> = Signal(false),
    dismiss: suspend () -> Unit = {},
) {
    col {
        val currentVerificationStep = remember {
            stepForVerificationForm(currentUser(), showSuccessIfComplete())
        }

        centered - danger - h1(Strings.verificationWelcome)

        swapView {
            swapping(
                current = { currentVerificationStep() },
                views = { step ->
                    when (step) {
                        VerificationStep.StepZeroCreateAccount -> expanding - col {
                            centered - critical - h4(Strings.firstCreateAccount)
                            centered - WarningSemantic.plus(ButtonSemantic).onNext - button {
                                centered - text(Strings.createAnAccount)
                                onClick {
                                    pageNavigator.reset(LogInScreen())
                                }
                            }
                        }

                        VerificationStep.StepOneHowWillYouPay -> expanding - col {
                            centered - critical - h4(Strings.setUpPayment)
                            expanding - space()
                            centered - h4(Strings.stepOne)
                            centered - h3(Strings.paymentMethodOne)

                            val financingSource = Signal<FinancingSource?>(null)
                            centered - rowCollapsingToColumn(60.rem) {
                                WarningSemantic.plus(ButtonSemantic).onNext - button {
                                    val value = FinancingSource.Cash
                                    centered - bold - text(Strings.financingSourceForm(value))
                                    onClick { financingSource.value = FinancingSource.Cash }
                                }
                                WarningSemantic.plus(ButtonSemantic).onNext - button {
                                    val value = FinancingSource.OwnFinancing
                                    centered - bold - text(Strings.financingSourceForm(value))
                                    onClick { financingSource.value = FinancingSource.OwnFinancing }
                                }
                                WarningSemantic.plus(ButtonSemantic).onNext - button {
                                    val value = FinancingSource.IlussoFinancing
//                dynamicTheme {
//                    if (financingSource() == value) SelectedSemantic else UnselectedSemantic
//                }
                                    centered - bold - text(Strings.financingSourceForm(value))

                                    onClick {
                                        financingSource.value = FinancingSource.IlussoFinancing
                                        dismiss()
                                        mainPageNavigator.navigate(FinancingFormScreen())
                                    }
                                }
                            }
                            lazyExpanding(financingSource.equalTo(FinancingSource.Cash)) {
                                val submit = Action(Strings.submit, Icon.done) {
                                    currentUser()?._id?.let {
                                        currentUserSession().user[it].modify(modification {
                                            it.financingSource assign FinancingSource.Cash
                                        })
                                    }
                                }
                                important - align(Align.Center, Align.Center) - button {
                                    text("Save and Continue")
                                    action = submit
                                }
                            }
                            lazyExpanding(financingSource.equalTo(FinancingSource.OwnFinancing)) {
                                val prop = Signal(ExternalFinancingForm())
                                val submit = Action(Strings.submit, Icon.done) {
                                    val form = prop().copy(user = currentUser()?._id ?: nullUuid)
                                    currentUserSession().externalFinancingForm.insert(form)
                                    currentUser()?._id?.let {
                                        currentUserSession().user[it].modify(modification {
                                            it.financingSource assign FinancingSource.OwnFinancing
                                            it.currentExternalFinancingForm assign form._id
                                        })
                                    }
                                }
                                col {
                                    externalFinancingForm(prop)
                                    important - align(Align.Center, Align.Center) - button {
                                        text("Save and Continue")
                                        action = submit
                                    }
                                }
                            }
                            lazyExpanding(financingSource.equalTo(FinancingSource.IlussoFinancing)) {
                                col {

                                }
                            }
                        }

                        VerificationStep.StepTwoCreditCardVerify -> expanding - col {
                            centered - critical - h4(Strings.setUpPayment)
                            expanding - space()
                            centered - h4(Strings.stepTwo)
                            centered - row {
                                centered - h3(Strings.paymentMethodTwo)
                                centered - sizeConstraints(1.5.rem, 1.5.rem) - icon {
                                    source = Icon.help
                                } in hintPopover(preferredDirection = PopoverPreferredDirection.aboveCenter) {
                                    sizeConstraints(maxWidth = 12.rem) - col {
                                        centered - text(Strings.creditCardVerificationTooltip)
                                    }
                                }
                            }
                            centered - col {
                                ::shown { currentUser()?.paymentSetup != null }
                                centered - AffirmativeSemantic.onNext - icon(Icon.done, "verification complete")
                                centered - AffirmativeSemantic.onNext -  h3("Credit card verification complete")
                                WarningSemantic.plus(ButtonSemantic).onNext - button {
                                    centered - text("Continue")
                                    onClick {
                                        showSuccessIfComplete set false
                                        dismiss()
                                    }
                                }
                            }
                            centered - WarningSemantic.plus(ButtonSemantic).onNext - externalLink {
                                ::shown { currentUser()?.paymentSetup == null }
                                val stripeUrl = rememberSuspending { currentUserSession().uncached.startStripe() }
                                ::to { stripeUrl() }
                                centered - text("Add Credit Card")
                            }
                            centered - button {
                                text("Back")
                                onClick {
                                    currentUser()?._id?.let {
                                        currentUserSession().user[it].modify(modification {
                                            it.financingSource assign null
                                        })
                                    }
                                }
                            }
                        }

                        else -> col {

                        }
                    }
                }
            )
        }
    }
}

class VerificationDialog(
    val fromStripe: MutableReactive<Boolean> = Signal(false)
) : Page {
    override fun ViewWriter.render() = dismissBackground {
        centered - sizedBox(SizeConstraints(maxWidth = 80.rem, minWidth = 20.rem)) - card - scrolling - col {
            paddingByEdge = Edges(50.dp, 30.dp, 50.dp, 30.dp)

            suspend fun dismissDialog() {
                if (masqueradeUserId()?.isNotEmpty() != true) {
                    currentUserSessionNullable()?.user[currentUser.awaitNotNull()._id]?.modify(modification {
                        it.firstLogin assign now()
                    })
                }
                dialogPageNavigator.dismiss()
            }

            verificationForm(fromStripe, ::dismissDialog)
            row {
                ::shown {
                    currentUser()?.let { it.paymentSetup != null } != true
                }
                expanding - space()
                compact - button {
                    centered - underline - critical - text("I'll do this later")
                    onClick {
                        dismissDialog()
                    }
                }
                expanding - space()
            }
        }
    }
}

fun ViewWriter.externalFinancingForm(form: Signal<ExternalFinancingForm>) {
    centered - col {
        label {
            content = Strings.whatBank
            FieldSemantic.plus(SelectedSemantic).onNext - textInput {
                content bind form.lensPath { it.bankName }
            }
        }
        label {
            content = Strings.howMuch
            gravity(Align.Start, Align.Center) - priceField(form.lensPath { it.approvalAmount })
        }
        label {
            content = Strings.howLong
            sizeConstraints(width = 6.5.rem) - compact - fieldTheme - numberInput {
                content bind form.lensPath { it.termLengthInMonths }.asDouble()
            }
        }
        label {
            content = Strings.whatInterestRate
            gravity(Align.Start, Align.Center) - compact - fieldTheme - row {
                centered - sizeConstraints(width = 6.5.rem) - numberInput {
                    align = Align.End
                    range = 0.0..100.0
                    content bind remember { form().interestRate?.times(100) }.withWrite { value ->
                            form.modify {
                                it.copy(
                                    interestRate = value?.div(100)
                                )
                            }
                        }
                }
                centered - text("%")
            }
        }
    }
}